/**
 * Centralized Error Handler
 * Single source of truth for error handling across the application
 */

import { log } from './logger';
import i18n from '@/shared/i18n/config';

export function normalizeCaughtError(thrownValue: unknown): Error {
  if (thrownValue instanceof Error) {
    return thrownValue;
  }

  if (typeof thrownValue === 'string') {
    return new Error(thrownValue);
  }

  try {
    const serializedValue = JSON.stringify(thrownValue);
    return new Error(serializedValue === undefined ? String(thrownValue) : serializedValue);
  } catch {
    try {
      return new Error(String(thrownValue));
    } catch {
      return new Error(i18n.t('Unknown error'));
    }
  }
}

export interface ErrorOptions {
  /** Context where the error occurred (component/module name) */
  context: string;
  /** User-friendly error message */
  userMessage?: string;
  /** Whether to show alert to user */
  showAlert?: boolean;
  /** Whether to log the error */
  shouldLog?: boolean;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

export interface HandleErrorResult {
  message: string;
  isNetworkError: boolean;
  isValidationError: boolean;
  statusCode?: number;
}

/**
 * Extract user-friendly message from error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }
  }

  return i18n.t('An unexpected error occurred');
}

/**
 * Check if error is a network error
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  if (error && typeof error === 'object' && 'name' in error) {
    return error.name === 'NetworkError' || error.name === 'FetchError';
  }

  return false;
}

/**
 * Check if error is a validation error
 */
function isValidationError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    if ('statusCode' in error && error.statusCode === 400) {
      return true;
    }
    if ('status' in error && error.status === 400) {
      return true;
    }
  }

  return false;
}

/**
 * Extract status code from error
 */
function getStatusCode(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      return error.statusCode;
    }
    if ('status' in error && typeof error.status === 'number') {
      return error.status;
    }
  }

  return undefined;
}

/**
 * Central error handler
 * Logs errors and optionally shows user-friendly messages
 */
export function handleError(error: unknown, options: ErrorOptions): HandleErrorResult {
  const {
    context,
    userMessage,
    showAlert = false,
    shouldLog = true,
    metadata = {}
  } = options;

  // Extract error details
  const errorMessage = getErrorMessage(error);
  const isNetwork = isNetworkError(error);
  const isValidation = isValidationError(error);
  const statusCode = getStatusCode(error);

  // Determine final message
  let finalMessage = userMessage || errorMessage;

  // Enhance message for network errors
  if (isNetwork && !userMessage) {
    finalMessage = i18n.t('Network error. Please check your connection and try again.');
  }

  // Log error
  if (shouldLog) {
    log.error(context, finalMessage, error, {
      ...metadata,
      isNetworkError: isNetwork,
      isValidationError: isValidation,
      statusCode
    });
  }

  // Show alert if requested
  if (showAlert) {
    alert(finalMessage);
  }

  return {
    message: finalMessage,
    isNetworkError: isNetwork,
    isValidationError: isValidation,
    statusCode
  };
}
